// Chunk loaders for the public routes that need the Firebase SDK (sign-in,
// SSO landing, counselor booking, shared essays). Keeping them out of the
// public shell means the landing and marketing pages paint without
// downloading or initializing Firestore/Auth/Storage. Because nothing in the
// shell initializes Firebase, each of these routes must import the initialized
// client (firebase.client) rather than call getFirestore()/getAuth() bare.
//
// This module must stay tiny: App.js imports it statically so a direct visit
// to one of these URLs can start the route chunk in parallel with the shell.
const loadedRouteComponents = new Map();

// Remember each route's component once its chunk has loaded, so a later
// client-side visit (e.g. clicking "Log in" after the idle warm-up) renders it
// synchronously instead of flashing a loading screen.
const recordingLoader = (importRoute) => {
  const load = () => importRoute().then((module) => {
    if (module && module.default) loadedRouteComponents.set(load, module.default);
    return module;
  });
  return load;
};

export const loadLogin = recordingLoader(() => import("./updated_frontpage/Login/Login"));
export const loadRegisterSSO = recordingLoader(() => import("./RegisterSSO.js"));
export const loadEssayShareViewer = recordingLoader(() => import("./updated_frontpage/EssayShare/EssayShareViewer"));
export const loadPublicCounselorBookingPage = recordingLoader(() => import("./booking/PublicCounselorBookingPage"));
export const loadStudentInviteJoin = recordingLoader(() => import("./StudentInviteJoin"));

/** The route component if its chunk has already loaded, else null. */
export const loadedPublicRouteComponent = (load) => loadedRouteComponents.get(load) || null;

const DIRECT_ENTRY_LOADERS = [
  [/^\/login\/?$/i, loadLogin],
  [/^\/auth\/?$/i, loadRegisterSSO],
  [/^\/essay-share\/?$/i, loadEssayShareViewer],
  [/^\/book(?:\/[^/]+)?\/?$/i, loadPublicCounselorBookingPage],
  [/^\/join\/?$/i, loadStudentInviteJoin],
];

/** Start (never await) the chunk for a Firebase-backed public route. */
export function preloadPublicRouteForPath(pathname) {
  const path = String(pathname || "");
  const match = DIRECT_ENTRY_LOADERS.find(([pattern]) => pattern.test(path));
  if (!match) return false;
  // Prefetch only: a failure here is repeated, and recovered, by the route's
  // own lazy render.
  match[1]().catch(() => {});
  return true;
}
